import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../components/AuthContext';
import Spinner from '../components/shared/Spinner';
import {
  getAllTickets,
  getTicketMessages,
  postAdminReply,
  requestAiReply,
  setTicketStatus,
} from '../lib/support';

// Admin-only panel shown inside the AdminPage "Support" tab. Shows the full
// queue of tickets across all users; RLS on support_tickets (admin policy)
// lets admins read everything while keeping the data private from regular
// users. Admins can reply, mark resolved, or ask Claude for a draft reply.

const STATUS_COLORS = {
  open:           '#ffa804',
  ai_answered:    '#6C7FFF',
  awaiting_user:  '#ffa804',
  escalated:      '#FF5555',
  resolved:       '#4ade80',
  closed:         'rgba(255,255,255,0.35)',
};

const STATUS_FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'escalated',  label: 'Needs human' },
  { id: 'open',       label: 'Open' },
  { id: 'ai_answered',label: 'AI answered' },
  { id: 'resolved',   label: 'Resolved' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

export default function AdminSupportPanel() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('escalated');
  const [active, setActive]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTickets(await getAllTickets()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return tickets;
    return tickets.filter(t => t.status === filter);
  }, [tickets, filter]);

  const counts = useMemo(() => {
    const c = { all: tickets.length };
    for (const t of tickets) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tickets]);

  return (
    <>
      <div className="admin-cost-grid">
        <StatCard label="Tickets total"      value={counts.all || 0} />
        <StatCard label="Needs human"        value={counts.escalated || 0} warn={(counts.escalated || 0) > 0} />
        <StatCard label="Open"               value={counts.open || 0} />
        <StatCard label="AI answered"        value={counts.ai_answered || 0} />
        <StatCard label="Resolved"           value={counts.resolved || 0} />
      </div>

      <div className="lib-filters" style={{ marginTop: 20 }}>
        <div className="lib-filter-group">
          {STATUS_FILTERS.map(s => (
            <button
              key={s.id}
              className={`lib-filter-chip${filter === s.id ? ' active' : ''}`}
              onClick={() => setFilter(s.id)}
            >
              {s.label}
              {counts[s.id === 'all' ? 'all' : s.id] ? ` · ${counts[s.id === 'all' ? 'all' : s.id]}` : ''}
            </button>
          ))}
        </div>
        <button className="btn-ghost" onClick={load} style={{ marginLeft: 'auto' }}>
          Refresh
        </button>
      </div>

      <div className="admin-section-card" style={{ padding: 0, marginTop: 16 }}>
        <div className="lib-table-header" style={{ gridTemplateColumns: '2fr 110px 110px 120px 80px', padding: '14px 20px' }}>
          <div>Subject</div>
          <div>Category</div>
          <div>Status</div>
          <div>Last activity</div>
          <div></div>
        </div>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--muted)', fontSize: 13 }}>Loading tickets…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--muted)', fontSize: 13 }}>No tickets in this view.</div>
        ) : (
          filtered.map(t => (
            <div key={t.id} className="lib-episode-row" style={{ gridTemplateColumns: '2fr 110px 110px 120px 80px', padding: '14px 20px', cursor: 'pointer' }} onClick={() => setActive(t)}>
              <div className="lib-ep-topic">{t.subject}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>{t.category}</div>
              <div>
                <span style={{ color: STATUS_COLORS[t.status] || 'var(--muted)', fontSize: 12, fontWeight: 600 }}>
                  {t.status}
                </span>
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>{timeAgo(t.last_message_at || t.created_at)}</div>
              <div>
                <button className="lib-ep-open-btn" onClick={(e) => { e.stopPropagation(); setActive(t); }}>Open</button>
              </div>
            </div>
          ))
        )}
      </div>

      {active && (
        <AdminTicketDrawer
          ticket={active}
          adminUserId={user?.id}
          onClose={() => { setActive(null); load(); }}
        />
      )}
    </>
  );
}

function StatCard({ label, value, warn }) {
  return (
    <div className="admin-cost-card">
      <div className="admin-cost-label">{label}</div>
      <div className="admin-cost-value" style={warn ? { color: '#FF5555' } : undefined}>{value}</div>
    </div>
  );
}

// ─── Ticket drawer (admin reply + AI draft) ────────────────────────────────
function AdminTicketDrawer({ ticket, adminUserId, onClose }) {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [reply, setReply]         = useState('');
  const [sending, setSending]     = useState(false);
  const [drafting, setDrafting]   = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessages(await getTicketMessages(ticket.id));
    setLoading(false);
  }, [ticket.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const handleDraft = async () => {
    setDrafting(true);
    try {
      const { reply: draft } = await requestAiReply(ticket, messages, null);
      if (draft) setReply(draft);
    } catch (err) {
      console.error('AI draft failed:', err);
      alert('Could not draft reply: ' + (err?.message || 'unknown'));
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async (markResolved) => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await postAdminReply(adminUserId, ticket.id, reply.trim(), { markResolved });
      setReply('');
      await load();
    } catch (err) {
      console.error('postAdminReply error:', err);
      alert('Failed to send: ' + (err?.message || 'unknown'));
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (s) => {
    try {
      await setTicketStatus(ticket.id, s);
      onClose?.();
    } catch (err) {
      alert('Failed: ' + (err?.message || 'unknown'));
    }
  };

  return (
    <div className="admin-ticket-drawer-overlay" onClick={onClose}>
      <div className="admin-ticket-drawer" onClick={e => e.stopPropagation()}>
        <div className="admin-ticket-drawer-header">
          <div>
            <div className="admin-ticket-drawer-title">{ticket.subject}</div>
            <div className="admin-ticket-drawer-sub">
              {ticket.category} · priority {ticket.priority} · status {ticket.status}
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>

        <div className="support-thread admin-ticket-drawer-thread" ref={threadRef}>
          {loading ? (
            <Spinner center />
          ) : messages.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--muted)' }}>No messages yet.</div>
          ) : (
            messages.map(m => (
              <div key={m.id} className={`support-msg support-msg-${m.sender_type}`}>
                <div className="support-msg-meta">
                  <span className="support-msg-sender">
                    {m.sender_type === 'user' ? 'User' :
                     m.sender_type === 'ai'   ? 'AI agent' :
                     m.sender_type === 'admin'? 'Admin' : 'System'}
                  </span>
                  <span className="support-msg-time">{timeAgo(m.created_at)}</span>
                </div>
                <div className="support-msg-body">{m.body}</div>
              </div>
            ))
          )}
        </div>

        <div className="admin-ticket-drawer-reply">
          <textarea
            className="support-textarea"
            rows={4}
            placeholder="Reply as Epio team…"
            value={reply}
            onChange={e => setReply(e.target.value)}
          />
          <div className="admin-ticket-drawer-actions">
            <button type="button" className="btn-ghost" onClick={handleDraft} disabled={drafting}>
              {drafting ? 'Drafting…' : 'AI draft reply'}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" onClick={() => handleStatus('resolved')}>
              Mark resolved
            </button>
            <button type="button" className="btn-ghost" onClick={() => handleSend(false)} disabled={sending || !reply.trim()}>
              {sending ? 'Sending…' : 'Send reply'}
            </button>
            <button type="button" className="btn-primary" onClick={() => handleSend(true)} disabled={sending || !reply.trim()}>
              Send & resolve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
